import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { VideoRecorder } from "@/components/VideoRecorder";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOW_LONG = [
  { v: "under_1y", l: "Under a year" },
  { v: "1_3y", l: "1 – 3 years" },
  { v: "3_10y", l: "3 – 10 years" },
  { v: "over_10y", l: "Over 10 years" },
];

export default function VouchSubmit() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    voucher_name: "", voucher_email: "", relationship: "friend",
    how_long_known: "3_10y", kind_story: "", would_recommend: true, video_url: null,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`${API}/vouches/invite/${token}`)
      .then((r) => {
        setInv(r.data);
        setForm((f) => ({ ...f, voucher_name: r.data.invitee_name || "", relationship: r.data.relationship || "friend" }));
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/vouches/invite/${token}/submit`, form);
      setDone(true);
      toast.success("Vouch submitted. Thank you.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  if (err && !inv) return (
    <div className="min-h-screen flex items-center justify-center paper px-6">
      <div className="card-paper p-10 max-w-md text-center">
        <div className="eyebrow">Vouch invitation</div>
        <h1 className="font-display text-3xl mt-3">{err}</h1>
      </div>
    </div>
  );
  if (!inv) return <div className="min-h-screen flex items-center justify-center paper text-ink/50">Loading…</div>;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center paper px-6">
      <div className="card-paper p-12 max-w-xl text-center">
        <div className="seal mx-auto mb-6">Vouch<br/><em>Received</em></div>
        <h1 className="font-display text-4xl text-ink" style={{ fontWeight: 400 }}>Thank you.</h1>
        <p className="mt-4 text-ink/70 leading-relaxed">
          Your vouch has been added to <strong>{inv.member_name}</strong>'s Wholesome application. Because of you, they're one step closer to their License to Love.
        </p>
        <div className="mt-6 font-script text-2xl text-ochre-300" style={{ fontFamily: "Caveat" }}>Kindness compounds.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen paper py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <Logo />
        <div className="card-paper p-10 mt-8">
          <div className="eyebrow">Vouch invitation · <span className="font-mono normal-case tracking-normal">{inv.member_license_no}</span></div>
          <h1 className="font-display text-4xl mt-3 text-ink" style={{ fontWeight: 400 }}>
            {inv.member_name} has asked you to <em className="italic text-ivy">vouch for them.</em>
          </h1>
          <p className="mt-4 text-ink/70 leading-relaxed">
            Wholesome is a different kind of dating platform — one where every member must be vouched for by three people who know them well. Your words will become part of <strong>{inv.member_name}</strong>'s profile. Please be honest, warm, and specific.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-ink/70">Your name</label>
                <input data-testid="vs-name" required value={form.voucher_name} onChange={set("voucher_name")}
                  className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" />
              </div>
              <div>
                <label className="text-xs font-medium text-ink/70">Your email (optional)</label>
                <input data-testid="vs-email" type="email" value={form.voucher_email} onChange={set("voucher_email")}
                  className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-ink/70">Your relationship</label>
                <select data-testid="vs-rel" value={form.relationship} onChange={set("relationship")}
                  className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10">
                  <option value="family">Family</option>
                  <option value="friend">Friend</option>
                  <option value="colleague">Colleague</option>
                  <option value="ex_partner">Ex-partner</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink/70">How long have you known them?</label>
                <select data-testid="vs-how-long" value={form.how_long_known} onChange={set("how_long_known")}
                  className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10">
                  {HOW_LONG.map((h) => <option key={h.v} value={h.v}>{h.l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-ink/70">
                Tell us why {inv.member_name} would make a wonderful partner. Share one specific story of them being kind.
              </label>
              <textarea data-testid="vs-story" required minLength={40} value={form.kind_story} onChange={set("kind_story")}
                rows={7} placeholder="I've known them since university and one story stays with me…"
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white border border-ink/10 leading-relaxed" />
              <div className="text-xs text-ink/50 mt-1">{form.kind_story.length} characters · minimum 40, ideal 200+</div>
            </div>

            <div>
              <label className="text-xs font-medium text-ink/70 mb-2 block">Add a video vouch (optional, up to 60 seconds)</label>
              <VideoRecorder token={token} onUpload={(url) => setForm((f) => ({ ...f, video_url: url }))} />
            </div>

            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" data-testid="vs-recommend" checked={form.would_recommend} onChange={(e) => setForm((f) => ({ ...f, would_recommend: e.target.checked }))} />
              <span>Yes, I would recommend {inv.member_name} as a genuine and kind partner.</span>
            </label>

            {err && <div className="text-sm text-ochre-300" data-testid="vs-err">{err}</div>}

            <button data-testid="vs-submit" type="submit" disabled={busy || form.kind_story.length < 40}
              className="btn-pill w-full bg-ivy text-parchment hover:bg-ivy-600 disabled:opacity-40">
              {busy ? "Submitting..." : "Submit your vouch"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
